import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Upload, Camera, FileText, Loader2, CheckCircle, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Image compression (avoids ingress timeouts on large phone photos) ----------
// Resizes any image to max 2000px on the long edge and re-encodes as JPEG q=0.82.
// Typical reduction: 8MB iPhone HEIC/JPG → ~600KB JPEG.
const compressImage = (file) => new Promise((resolve, reject) => {
  if (!file.type?.startsWith("image/")) { resolve(file); return; }
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    const MAX = 2000;
    let { width: w, height: h } = img;
    if (w > MAX || h > MAX) {
      const scale = MAX / Math.max(w, h);
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) { resolve(file); return; }
        const compressed = new File([blob], (file.name || "report").replace(/\.[^.]+$/, "") + ".jpg", {
          type: "image/jpeg", lastModified: Date.now(),
        });
        // Keep the smaller of the two
        resolve(compressed.size < file.size ? compressed : file);
      },
      "image/jpeg", 0.82,
    );
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
  img.src = url;
});

// Program launches Monday 2026-06-22 (no reports allowed before this date)
const PROGRAM_START = "2026-06-22";

// US date display helpers (MM/DD/YYYY)
const usDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
};
const usDateLong = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

// POS departments in the order they appear on the printed report
const POS_DEPTS = [
  { code: "002", name: "SODA",            group: "GROCERY" },
  { code: "003", name: "GROCERY",         group: "GROCERY" },
  { code: "004", name: "NON FOOD",        group: "GROCERY" },
  { code: "005", name: "PRODUCE",         group: "PRODUCE" },
  { code: "006", name: "DELI",            group: "DELI & PREPARED" },
  { code: "007", name: "FROZEN FOODS",    group: "FROZEN & DAIRY" },
  { code: "009", name: "DAIRY",           group: "FROZEN & DAIRY" },
  { code: "010", name: "FISH",            group: "MEAT" },
  { code: "011", name: "MISCELLANEOUS",   group: "GROCERY" },
  { code: "012", name: "DRY FRUIT",       group: "DELI & PREPARED" },
  { code: "013", name: "BAKERY",          group: "DELI & PREPARED" },
  { code: "014", name: "FROZEN FISH",     group: "MEAT" },
  { code: "015", name: "ICE CREAM",       group: "FROZEN & DAIRY" },
  { code: "016", name: "EGGS",            group: "FROZEN & DAIRY" },
  { code: "017", name: "MEAT",            group: "MEAT" },
  { code: "018", name: "MILK",            group: "FROZEN & DAIRY" },
  { code: "019", name: "ORGANIC PRODUCE", group: "PRODUCE" },
  { code: "020", name: "PACKAGED FISH",   group: "MEAT" },
  { code: "021", name: "PREPARED FOODS",  group: "DELI & PREPARED" },
  { code: "082", name: "BAG TAX",         group: null },
];

const GROUP_COLORS = {
  "PRODUCE":         "bg-green-100 text-green-900 border-green-300",
  "MEAT":            "bg-red-100 text-red-900 border-red-300",
  "DELI & PREPARED": "bg-yellow-100 text-yellow-900 border-yellow-300",
  "GROCERY":         "bg-purple-100 text-purple-900 border-purple-300",
  "FROZEN & DAIRY":  "bg-blue-100 text-blue-900 border-blue-300",
};

function mondayOf(d) {
  const dt = new Date(d + "T00:00:00");
  const day = dt.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  dt.setDate(dt.getDate() + diff);
  return dt.toISOString().slice(0, 10);
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

// Default week start: the most recent Monday on/after PROGRAM_START.
// Before launch → defaults to PROGRAM_START. After launch → current week's Monday.
function defaultWeekStart() {
  const today = todayISO();
  const todaysMonday = mondayOf(today);
  return todaysMonday >= PROGRAM_START ? todaysMonday : PROGRAM_START;
}

export default function WeeklyReportPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(sessionStorage.getItem("master_authed") === "true");
  const [pin, setPin] = useState("");
  const [mode, setMode] = useState("photo"); // "photo" | "manual"
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);
  const [recent, setRecent] = useState([]);

  // Manual entry state
  const [weekStart, setWeekStart] = useState(defaultWeekStart());
  const [manual, setManual] = useState({
    net_sales: "", total_sales: "",
    customers: "", avg_sale_per_customer: "",
    items_sold: "", avg_items_per_customer: "", avg_item_price: "",
    cancels: "", returns: "", coupons: "", discounts: "",
    non_netted_promotion: "", netted_promotion: "",
  });
  const [deptRows, setDeptRows] = useState(POS_DEPTS.map(d => ({ ...d, gross_sales: "", net_sales: "", pct_gross: "", pct_net: "" })));

  useEffect(() => {
    if (authed) loadRecent();
  }, [authed]);

  const handlePinSubmit = () => {
    if (["83528", "4817", "7861", "1111"].includes(pin)) {
      sessionStorage.setItem("master_authed", "true");
      setAuthed(true);
    } else {
      toast.error("Invalid code");
    }
  };

  const loadRecent = async () => {
    try {
      const r = await axios.get(`${API}/weekly-report/list`);
      setRecent(r.data.reports || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setReport(null);
    try {
      // Compress huge phone photos before upload (prevents proxy timeouts on production)
      let toSend = file;
      try {
        toSend = await compressImage(file);
        if (toSend !== file) {
          const before = (file.size / 1024 / 1024).toFixed(1);
          const after = (toSend.size / 1024 / 1024).toFixed(1);
          console.log(`Compressed photo: ${before}MB → ${after}MB`);
        }
      } catch (compressErr) {
        console.warn("Compression skipped:", compressErr);
      }

      const fd = new FormData();
      fd.append("file", toSend);
      fd.append("created_by", "phone");
      const res = await axios.post(`${API}/weekly-report/upload-photo`, fd, {
        timeout: 120000,    // 2 min — Gemini Vision can take 30-60s on busy days
      });
      setReport(res.data.report);
      if (res.data.date_clamped) {
        toast.warning(`⚠️ AI read the wrong week date — saved as ${usDate(res.data.report.week_start)}. Check Manual Entry below if wrong.`, { duration: 9000 });
      } else {
        toast.success(`Extracted ${res.data.extracted_dept_count} departments from photo`);
      }
      loadRecent();
    } catch (err) {
      // Self-healing: even if axios saw an error (CF 502 / timeout / network blip),
      // the backend may have already saved the report. Wait briefly and re-check.
      try {
        await new Promise(r => setTimeout(r, 2500));
        const check = await axios.get(`${API}/weekly-report/list`, { params: { limit: 5 } });
        const recent = check.data?.reports || [];
        // A report saved within the last 2 minutes (matches the axios timeout window)
        const twoMinAgo = Date.now() - 120_000;
        const saved = recent.find(r => {
          const created = new Date(r.created_at || r.updated_at || 0).getTime();
          return created >= twoMinAgo;
        });
        if (saved) {
          setReport(saved);
          loadRecent();
          toast.success(
            `✅ Upload actually succeeded! Saved ${saved.departments?.length || 0} departments for week ${usDate(saved.week_start)}.`,
            { duration: 7000 }
          );
          return;
        }
      } catch { /* ignore self-heal probe error */ }

      const detail = err?.response?.data?.detail || err.message || "Unknown error";
      const status = err?.response?.status;
      // Friendly, actionable message
      let msg = `Upload failed: ${detail}`;
      if (err.code === "ECONNABORTED" || /timeout/i.test(err.message || "")) {
        msg = "⏱️ Upload took too long. Try a smaller/clearer photo, or use Manual Entry below.";
      } else if (status === 413 || /too large/i.test(detail)) {
        msg = "📏 Photo too large. We compress automatically — try again or use Manual Entry below.";
      } else if (/before the program launch/i.test(detail)) {
        msg = "📅 AI read the wrong week from the photo. Scroll to Manual Entry below to set the correct week.";
      } else if (/non-?json|extractor/i.test(detail)) {
        msg = "🤖 AI couldn't read this photo clearly. Try a sharper one in good light, or use Manual Entry below.";
      } else if (status >= 500) {
        msg = "🌐 The server is taking too long or is temporarily unavailable. Try again in a moment, or use Manual Entry below.";
      } else if (!status) {
        msg = "🌐 Network hiccup. Check your connection and try again. Manual Entry works offline-safe.";
      }
      toast.error(msg, { duration: 8000 });
      console.error("Upload failed:", err);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const loadReportIntoEditor = (r) => {
    // Pre-fill manual entry form so user can correct any number
    setWeekStart(r.week_start);
    setManual({
      net_sales: r.net_sales || "",
      total_sales: r.total_sales || "",
      customers: r.health?.customers || "",
      avg_sale_per_customer: r.health?.avg_sale_per_customer || "",
      items_sold: r.health?.items_sold || "",
      avg_items_per_customer: r.health?.avg_items_per_customer || "",
      avg_item_price: r.health?.avg_item_price || "",
      cancels: r.health?.cancels || "",
      returns: r.health?.returns || "",
      coupons: r.health?.coupons || "",
      discounts: r.health?.discounts || "",
      non_netted_promotion: r.health?.non_netted_promotion || "",
      netted_promotion: r.health?.netted_promotion || "",
    });
    // Map saved departments onto the editable rows (match by name fragment)
    setDeptRows(POS_DEPTS.map(def => {
      const found = (r.departments || []).find(
        d => (d.name || "").toUpperCase().includes(def.name.toUpperCase().split("/")[0]) ||
             def.name.toUpperCase().includes((d.name || "").toUpperCase().split(" ")[0])
      );
      return {
        ...def,
        gross_sales: found?.gross_sales || "",
        net_sales: found?.net_sales || "",
        pct_gross: found?.pct_gross || "",
        pct_net: found?.pct_net || "",
      };
    }));
    setReport(r);
    setMode("manual");
    toast.info(`Now editing ${usDate(r.week_start)} report. Fix any numbers below, then click Save.`, { duration: 6000 });
    // Scroll to manual form
    setTimeout(() => {
      document.querySelector('[data-testid="mode-manual"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
  };

  const submitManual = async () => {
    const num = (v) => parseFloat(v) || 0;
    const int = (v) => parseInt(v) || 0;
    const payload = {
      week_start: weekStart,
      net_sales: num(manual.net_sales),
      total_sales: num(manual.total_sales) || num(manual.net_sales),
      non_merchandise: 0,
      health: {
        customers: int(manual.customers),
        avg_sale_per_customer: num(manual.avg_sale_per_customer),
        items_sold: int(manual.items_sold),
        avg_items_per_customer: num(manual.avg_items_per_customer),
        avg_item_price: num(manual.avg_item_price),
        cancels: num(manual.cancels),
        returns: num(manual.returns),
        coupons: num(manual.coupons),
        discounts: num(manual.discounts),
        non_netted_promotion: num(manual.non_netted_promotion),
        netted_promotion: num(manual.netted_promotion),
      },
      departments: deptRows
        .filter(r => r.net_sales !== "" || r.gross_sales !== "")
        .map(r => ({
          code: r.code,
          name: r.name,
          gross_sales: num(r.gross_sales),
          net_sales: num(r.net_sales),
          pct_gross: num(r.pct_gross),
          pct_net: num(r.pct_net),
        })),
      created_by: "manual",
    };

    try {
      const res = await axios.post(`${API}/weekly-report/manual`, payload);
      setReport(res.data.report);
      toast.success(`Weekly report saved for week of ${weekStart}`);
      loadRecent();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Save failed: ${detail}`);
    }
  };

  // Group totals (live preview as user fills manual data)
  const groupPreview = useMemo(() => {
    const totals = { PRODUCE: 0, MEAT: 0, "DELI & PREPARED": 0, GROCERY: 0, "FROZEN & DAIRY": 0 };
    const source = report?.departments?.length ? report.departments : deptRows;
    source.forEach(d => {
      const grp = POS_DEPTS.find(p => p.code === d.code)?.group;
      if (grp && totals[grp] !== undefined) totals[grp] += parseFloat(d.net_sales) || 0;
    });
    const total = Object.values(totals).reduce((a, b) => a + b, 0);
    return { totals, total };
  }, [deptRows, report]);

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md">
          <h1 className="text-xl font-bold mb-2">Weekly Report</h1>
          <p className="text-sm text-stone-600 mb-4">Enter your master/manager code to continue.</p>
          <Input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handlePinSubmit()}
            placeholder="••••"
            className="text-center text-2xl tracking-widest h-14"
            data-testid="weekly-pin-input"
          />
          <Button className="w-full mt-3 h-12" onClick={handlePinSubmit} data-testid="weekly-pin-submit">Continue</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-white border-b border-stone-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate("/timecard")} className="flex items-center gap-2 text-stone-700 hover:text-[#A61B1B]" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="text-base font-bold text-[#1A1A1A]">Weekly Revenue Report</h1>
        <div className="w-12"></div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-4">
        {/* Mode toggle */}
        <div className="bg-white rounded-lg shadow-sm p-1 flex gap-1">
          <button
            onClick={() => setMode("photo")}
            className={`flex-1 py-3 rounded-md font-bold text-sm transition ${mode === "photo" ? "bg-[#A61B1B] text-white" : "text-stone-600 hover:bg-stone-100"}`}
            data-testid="mode-photo"
          ><Camera className="w-4 h-4 inline mr-1" /> Photo Upload</button>
          <button
            onClick={() => setMode("manual")}
            className={`flex-1 py-3 rounded-md font-bold text-sm transition ${mode === "manual" ? "bg-[#A61B1B] text-white" : "text-stone-600 hover:bg-stone-100"}`}
            data-testid="mode-manual"
          ><FileText className="w-4 h-4 inline mr-1" /> Manual Entry</button>
        </div>

        {/* PHOTO MODE */}
        {mode === "photo" && (
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <div className="text-center">
              <Camera className="w-10 h-10 text-[#A61B1B] mx-auto mb-2" />
              <h2 className="text-base sm:text-lg font-bold mb-1">Snap or upload your POS Department Report</h2>
              <p className="text-sm text-stone-600 mb-4">Gemini Vision extracts every field. Works on phone or desktop.</p>

              {/* Three upload options — Camera, Gallery, Files */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 max-w-2xl mx-auto">
                <label className={`flex flex-col items-center justify-center gap-1.5 px-4 py-5 rounded-xl bg-[#A61B1B] text-white font-bold cursor-pointer hover:bg-[#841515] transition active:scale-[0.98] ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-camera-label">
                  {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Camera className="w-7 h-7" />}
                  <span className="text-sm">Take Live Photo</span>
                  <span className="text-[10px] font-normal text-white/80">Opens camera</span>
                  <input type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} className="hidden" data-testid="photo-upload-camera" />
                </label>

                <label className={`flex flex-col items-center justify-center gap-1.5 px-4 py-5 rounded-xl bg-[#1A1A1A] text-white font-bold cursor-pointer hover:bg-stone-800 transition active:scale-[0.98] ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-gallery-label">
                  {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-7 h-7" />}
                  <span className="text-sm">Choose from Gallery</span>
                  <span className="text-[10px] font-normal text-white/80">Photos & images</span>
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="photo-upload-gallery" />
                </label>

                <label className={`flex flex-col items-center justify-center gap-1.5 px-4 py-5 rounded-xl bg-stone-700 text-white font-bold cursor-pointer hover:bg-stone-800 transition active:scale-[0.98] ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-files-label">
                  {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <FileText className="w-7 h-7" />}
                  <span className="text-sm">Choose from Files</span>
                  <span className="text-[10px] font-normal text-white/80">Any image file</span>
                  <input type="file" accept="image/*,.png,.jpg,.jpeg,.webp" onChange={handlePhotoUpload} className="hidden" data-testid="photo-upload-files" />
                </label>
              </div>
              <p className="text-xs text-stone-500 mt-3">JPEG/PNG up to 15MB. {uploading && "Extracting with Gemini Vision…"}</p>
            </div>
          </div>
        )}

        {/* MANUAL MODE */}
        {mode === "manual" && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <label className="text-sm font-semibold">Week start (Monday):</label>
                <Input type="date" min={PROGRAM_START} value={weekStart} onChange={(e) => setWeekStart(mondayOf(e.target.value))} className="w-44" data-testid="week-start-input" />
                <span className="text-xs text-stone-500">
                  → Sunday {(() => { const d = new Date(weekStart + "T00:00:00"); d.setDate(d.getDate() + 6); return usDate(d.toISOString().slice(0, 10)); })()}
                </span>
                {weekStart < PROGRAM_START && (
                  <span className="text-xs text-red-700 bg-red-50 px-2 py-1 rounded font-semibold">
                    ⚠ Program launches {usDate(PROGRAM_START)} — earlier weeks rejected.
                  </span>
                )}
                <span className="ml-auto text-xs text-stone-600 font-mono">{usDate(weekStart)} → {usDate((() => { const d = new Date(weekStart + "T00:00:00"); d.setDate(d.getDate() + 6); return d.toISOString().slice(0, 10); })())}</span>
              </div>

              <h3 className="font-bold text-sm mb-2 mt-4">Top-of-report totals</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <ManualField label="Net Sales" value={manual.net_sales} set={(v) => setManual({...manual, net_sales: v})} prefix="$" testId="net-sales" />
                <ManualField label="Total Sales" value={manual.total_sales} set={(v) => setManual({...manual, total_sales: v})} prefix="$" testId="total-sales" />
                <ManualField label="# Customers" value={manual.customers} set={(v) => setManual({...manual, customers: v})} testId="customers" />
                <ManualField label="Avg Sale/Customer" value={manual.avg_sale_per_customer} set={(v) => setManual({...manual, avg_sale_per_customer: v})} prefix="$" testId="avg-sale" />
                <ManualField label="# Items Sold" value={manual.items_sold} set={(v) => setManual({...manual, items_sold: v})} testId="items-sold" />
                <ManualField label="Avg Items/Customer" value={manual.avg_items_per_customer} set={(v) => setManual({...manual, avg_items_per_customer: v})} testId="avg-items" />
                <ManualField label="Avg Item Price" value={manual.avg_item_price} set={(v) => setManual({...manual, avg_item_price: v})} prefix="$" testId="avg-item-price" />
                <ManualField label="Cancels" value={manual.cancels} set={(v) => setManual({...manual, cancels: v})} prefix="$" testId="cancels" />
                <ManualField label="Returns" value={manual.returns} set={(v) => setManual({...manual, returns: v})} prefix="$" testId="returns" />
                <ManualField label="Coupons" value={manual.coupons} set={(v) => setManual({...manual, coupons: v})} prefix="$" testId="coupons" />
                <ManualField label="Discounts" value={manual.discounts} set={(v) => setManual({...manual, discounts: v})} prefix="$" testId="discounts" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="font-bold text-sm mb-3">Department Revenue (enter Net Sales for each)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-100">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold">#</th>
                      <th className="text-left px-2 py-1.5 font-semibold">POS Dept</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Group</th>
                      <th className="text-right px-2 py-1.5 font-semibold">Gross $</th>
                      <th className="text-right px-2 py-1.5 font-semibold">Net $</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deptRows.map((d, i) => (
                      <tr key={d.code} className="border-b border-stone-100">
                        <td className="px-2 py-1 font-mono text-xs text-stone-500">{d.code}</td>
                        <td className="px-2 py-1 font-medium">{d.name}</td>
                        <td className="px-2 py-1">
                          {d.group ? (
                            <span className={`text-xs px-1.5 py-0.5 rounded border ${GROUP_COLORS[d.group]}`}>{d.group}</span>
                          ) : (
                            <span className="text-xs text-stone-400">skip</span>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          <Input type="number" step="0.01" value={d.gross_sales} onChange={(e) => {
                            const v = e.target.value; const arr = [...deptRows]; arr[i] = {...arr[i], gross_sales: v}; setDeptRows(arr);
                          }} className="h-8 text-right text-sm" placeholder="0.00" data-testid={`dept-gross-${d.code}`} />
                        </td>
                        <td className="px-2 py-1">
                          <Input type="number" step="0.01" value={d.net_sales} onChange={(e) => {
                            const v = e.target.value; const arr = [...deptRows]; arr[i] = {...arr[i], net_sales: v}; setDeptRows(arr);
                          }} className="h-8 text-right text-sm" placeholder="0.00" data-testid={`dept-net-${d.code}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button className="w-full mt-4 h-12 bg-[#A61B1B] hover:bg-[#841515]" onClick={submitManual} data-testid="submit-manual">
                <CheckCircle className="w-5 h-5 mr-2" /> Save Weekly Report
              </Button>
            </div>
          </div>
        )}

        {/* Live group preview */}
        {(report || deptRows.some(d => d.net_sales)) && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="font-bold text-sm mb-3">Live Revenue Allocation by Employee Group</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {Object.entries(groupPreview.totals).map(([grp, val]) => (
                <div key={grp} className={`rounded-lg border-2 p-3 ${GROUP_COLORS[grp]}`}>
                  <div className="text-xs font-bold uppercase tracking-wide">{grp}</div>
                  <div className="text-lg font-extrabold mt-1">${val.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                  <div className="text-xs opacity-80">{groupPreview.total ? ((val / groupPreview.total) * 100).toFixed(1) : 0}% of total</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Extracted result preview after photo upload */}
        {report && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-[#A61B1B]" />
              <h3 className="font-bold text-sm">Saved report — week of {usDateLong(report.week_start)}</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <MetricTile label="Net Sales" value={`$${(report.net_sales || 0).toLocaleString()}`} />
              <MetricTile label="Customers" value={(report.health?.customers || 0).toLocaleString()} />
              <MetricTile label="Avg Sale" value={`$${report.health?.avg_sale_per_customer?.toFixed(2) || 0}`} />
              <MetricTile label="Items Sold" value={(report.health?.items_sold || 0).toLocaleString()} />
            </div>
          </div>
        )}

        {/* Recent reports */}
        {recent.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="font-bold text-sm mb-3">Recent Weekly Reports</h3>
            <p className="text-xs text-stone-500 mb-2 italic">Numbers wrong? Tap <span className="font-semibold text-[#A61B1B]">Edit</span> to fix them.</p>
            <div className="space-y-1">
              {recent.slice(0, 8).map((r) => (
                <div key={r.week_start} className="flex items-center justify-between gap-2 p-2 rounded hover:bg-stone-50 text-sm">
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold">{usDate(r.week_start)}</span>
                    <span className="text-xs text-stone-500 ml-2">({r.source})</span>
                  </div>
                  <div className="font-mono whitespace-nowrap">${(r.net_sales || 0).toLocaleString()}</div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs"
                    onClick={() => loadReportIntoEditor(r)}
                    data-testid={`edit-report-${r.week_start}`}
                  >Edit</Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ManualField({ label, value, set, prefix, testId }) {
  return (
    <label className="block">
      <span className="text-xs text-stone-600">{label}</span>
      <div className="relative">
        {prefix && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-sm">{prefix}</span>}
        <Input
          type="number"
          step="0.01"
          value={value}
          onChange={(e) => set(e.target.value)}
          className={`h-9 ${prefix ? "pl-6" : ""}`}
          placeholder="0"
          data-testid={`manual-${testId}`}
        />
      </div>
    </label>
  );
}

function MetricTile({ label, value }) {
  return (
    <div className="bg-stone-50 rounded p-2">
      <div className="text-xs text-stone-500">{label}</div>
      <div className="text-base font-bold text-[#1A1A1A]">{value}</div>
    </div>
  );
}
